// Single source of truth for the Q-Biologics™ platform pipeline. Both the
// Products page (the phase chart) and the Bio-Ply page (development status
// track) read from here, so a status change only needs to be made once.
//
// Shape matches the design handoff's `pipeline-data.json`:
//   { stages: string[5], groups: [{ name, source, rows: [{ species, indication,
//     stage: 1–5, status? }] }] }
// `stage` is 1-INDEXED into STAGE_LABELS; `status` is an optional label override
// (used only for the asterisked "FDA pathway*").

// Ordered development stages every program moves through.
export const STAGE_LABELS = [
  "Discovery",
  "Preclinical",
  "Clinical trials",
  "FDA pathway",
  "FDA approved",
];

// Stage colour sets, indexed 1–5 to match `stage`. One warm brand ramp — deep
// maroon-red at Discovery warming through orange and resolving to cream at FDA
// approved — so the sequence itself communicates progression without a rail. Do
// not substitute unrelated hues (blue/green/purple) per stage.
//
// `dot` is the chip dot and legend bar; `bg`/`border` compose the chip; `text`
// is the chip/legend label. Every `dot` clears 3:1 against the #17161A band and
// against its own chip fill (WCAG 1.4.11) — #C94D5A is the floor at 3.6:1 on the
// chip, so do not darken it; the earlier #B23A46 failed at 2.8:1. Chip borders
// sit at .55–.65 alpha because lower alphas put the chip edge under 3:1.
export const STAGE_COLORS = {
  1: { dot: "#C94D5A", bg: "rgba(201,77,90,.13)", border: "rgba(201,77,90,.65)", text: "#E8969F" },
  2: { dot: "#D26244", bg: "rgba(210,98,68,.13)", border: "rgba(210,98,68,.65)", text: "#E9A28B" },
  3: { dot: "#D9773B", bg: "rgba(217,119,59,.13)", border: "rgba(217,119,59,.6)", text: "#EDAF85" },
  4: { dot: "#E8A544", bg: "rgba(232,165,68,.14)", border: "rgba(232,165,68,.55)", text: "#F2C689" },
  5: { dot: "#F5F3F0", bg: "rgba(245,243,240,.12)", border: "rgba(245,243,240,.6)", text: "#F5F3F0" },
};

// Legend printed under the chart. Repeats the stage → colour mapping in words so
// status is never carried by colour alone. Order matches STAGE_LABELS.
export const PIPELINE_LEGEND = [
  { label: "Discovery", desc: "Early research and proof-of-concept" },
  { label: "Preclinical", desc: "Laboratory and animal studies" },
  { label: "Clinical trials", desc: "Used in veterinary clinical trials" },
  { label: "FDA pathway*", desc: "Advancing through the FDA approval process" },
  { label: "FDA approved", desc: "Commercially available veterinary product" },
];

// Label shown in the status column and the hover tooltip.
export const stageStatusLabel = (row) => row.status || STAGE_LABELS[row.stage - 1];

// Required qualifier wherever the asterisked "FDA pathway*" status appears —
// participation in the FDA-CVM program is not FDA approval, and must never read
// as if it were. Split so the first clause can be emphasised.
export const FDA_PATHWAY_FOOTNOTE_LEAD =
  "*BIO-PLY™ is participating in the FDA Center for Veterinary Medicine (FDA-CVM) Animal Cell, Tissue, and Gene Therapy (ACTP) Veterinary Innovation Program.";

export const FDA_PATHWAY_FOOTNOTE_REST =
  "Participation reflects active collaboration with the FDA-CVM to support product development and regulatory review, and does not constitute FDA approval.";

export const FDA_PATHWAY_FOOTNOTE = `${FDA_PATHWAY_FOOTNOTE_LEAD} ${FDA_PATHWAY_FOOTNOTE_REST}`;

// Split around the "Contact us" link so the chart can render it as a real link
// to /contact without hard-coding the sentence in the page.
export const PIPELINE_DISCLAIMER_LEAD =
  "Development stages and timing are indicative and subject to change.";

export const PIPELINE_DISCLAIMER_LINK = "Contact us";

export const PIPELINE_DISCLAIMER_REST = "for the current status of any program.";

export const PIPELINE_DISCLAIMER = `${PIPELINE_DISCLAIMER_LEAD} ${PIPELINE_DISCLAIMER_LINK} ${PIPELINE_DISCLAIMER_REST}`;

// Each group is one peptide biologic (or family of them); `rows` are the
// species/indication programs being developed from it, each at its own stage.
export const PIPELINE_GROUPS = [
  {
    name: "BIO-PLY™",
    source: "Platelet-derived peptide biologic",
    rows: [
      {
        species: "Horse",
        indication: "Septic synovial infections",
        stage: 4,
        status: "FDA pathway*",
      },
      { species: "Horse", indication: "Endometritis", stage: 3 },
      { species: "Dog", indication: "Otitis externa", stage: 3 },
      { species: "Dog", indication: "Implant-associated infections", stage: 3 },
      { species: "Horse", indication: "Keratitis", stage: 1 },
    ],
  },
  {
    name: "NAP-X™",
    source: "Neutrophil-derived peptide biologic",
    rows: [{ species: "Horse", indication: "Respiratory infections", stage: 2 }],
  },
  {
    name: "Future peptide biologics",
    source: "Novel host-defense peptide platforms",
    rows: [
      { species: "Multiple species", indication: "Programs in evaluation", stage: 1 },
    ],
  },
];

// Look up a group by its display name (e.g. 'BIO-PLY™').
export const getGroup = (name) => PIPELINE_GROUPS.find((g) => g.name === name);

// BIO-PLY™'s lead program — the one the BIO-PLY page's development status track
// follows. Kept here so the page and the phase chart can never disagree.
export const BIO_PLY_LEAD = getGroup("BIO-PLY™").rows[0];
